import { useState, useCallback } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useQueryParam, StringParam } from 'use-query-params';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { TermFilterContext, ProjectTermsContext, HighlightQueryContext, TranscriptReplacementsContext, SelectedTermsContext } from './Context';
import type { TranscriptReplacement, GlobalReplacement, Mode, ConfirmedTerm } from './interfaces';

type Props = {
  children: React.ReactNode;
  projectId: number;
};

export const TermsContainer = (props: Props) => {
  const [searchTerm, setSearchTerm] = useQueryParam('term', StringParam, { updateType: 'replaceIn' });
  const [replacementTerm, setReplacementTerm] = useState('');
  const [highlightTerm, setHighlightTerm] = useQueryParam('highlight', StringParam, { updateType: 'replaceIn' });
  const [mode, changeMode] = useState<Mode>('normal');

  const [replacements, setReplacements] = useState<TranscriptReplacement[]>([]);
  const [globalReplacements, setGlobalReplacements] = useState<GlobalReplacement[]>([]);
  const [confirmedTerms, setConfirmedTerms] = useState<ConfirmedTerm[]>([]);
  const [selectedTermValues, setSelectedTerms] = useState<string[]>([]);
  const [dictionaryWords, setDictionaryWords] = useState<string[]>([]);

  const addReplacements = useCallback((replacements: TranscriptReplacement[]) => {
    setReplacements(old => [...old, ...replacements]);
  }, []);

  const addGlobalReplacements = useCallback((replacements: GlobalReplacement[]) => {
    setGlobalReplacements(old => [...old, ...replacements]);
  }, []);

  const addConfirmedTerms = useCallback((terms: ConfirmedTerm[]) => {
    setConfirmedTerms(old => [...old, ...terms.filter(t => !old.some(o => o.term === t.term))]);
  }, []);

  const toggleTermValue = useCallback((val: string) => {
    setSelectedTerms(old => {
      if (old.includes(val)) {
        return old.filter(x => x !== val);
      } else {
        return [...old, val];
      }
    });
  }, []);

  const clearTermValues = useCallback(() => {
    setSelectedTerms([]);
  }, []);

  const addDictionaryWord = useCallback((val: string) => {
    const trimmedVal = val.trim();
    if (!trimmedVal) return; // Don't add empty strings

    setDictionaryWords(old => {
      const exists = old.some(word => word.trim().toLowerCase() === trimmedVal.toLowerCase());
      return exists ? old : [...old, trimmedVal];
    });
  }, []);

  const apiClient = useChatApiClient();

  const projectTermsQuery = useQuery({
    queryKey: ['get-project-terms', props.projectId],
    queryFn: async () => {
      return apiClient.projects.fetchProjectTerms({ projectId: props.projectId });
    },
    placeholderData: { terms: [] },
    enabled: !!props.projectId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const infiniteHighlightsQuery = useInfiniteQuery({
    queryKey: ['get-project-term-highlights', props.projectId, highlightTerm],
    queryFn: ({ pageParam }) => {
      return apiClient.projects.fetchProjectTermHighlights({ projectId: props.projectId, term: highlightTerm, searchAfter: pageParam as string[] });
    },
    enabled: highlightTerm?.length > 0,
    placeholderData: { pages: [], pageParams: [] },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialPageParam: [],
    getNextPageParam: lastPage => lastPage.searchAfter,
  });

  return (
    <ProjectTermsContext.Provider value={{ query: projectTermsQuery }}>
      <TranscriptReplacementsContext.Provider value={{ replacements, addReplacements, projectId: props.projectId, mode, changeMode, globalReplacements, addGlobalReplacements, confirmedTerms, addConfirmedTerms, dictionaryWords, addDictionaryWord }}>
        <TermFilterContext.Provider value={{ searchTerm, setSearchTerm, replacement: replacementTerm, setReplacement: setReplacementTerm, highlightTerm, setHighlightTerm }}>
          <HighlightQueryContext.Provider value={{ query: infiniteHighlightsQuery }}>
            <SelectedTermsContext.Provider value={{ selectedTermValues, toggleTermValue, clearTermValues }}>
              {props.children}
            </SelectedTermsContext.Provider>
          </HighlightQueryContext.Provider>
        </TermFilterContext.Provider>
      </TranscriptReplacementsContext.Provider>
    </ProjectTermsContext.Provider>
  );
};